import * as React from 'react'
import * as Tooltip from 'react-tooltip'
import moment from 'moment'
import { orderBy } from 'lodash'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../../../stores/CurrentUserStore'
import { LoadingAnimation } from '../../../../../../components/LoadingAnimation'
import { EditEmployeeHourModal } from './EditEmployeeHourModal'
import { CreateEmployeeHourModal } from './CreateEmployeeHourModal'
import { API, IEmployeeHour, PermissionNames } from '@getgreenline/homi-shared'
import { parseAndDownloadCSV, GrTime } from '../../../../../../utilities/helpers'
import { ReportFilter } from '../../ReportFilter'
import { ReportFilterStore } from '../../ReportFilterStore'
import Helmet from 'react-helmet'
import { Track } from '../../../../../../Track'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
  reportFilterStore?: ReportFilterStore
}

type SortFields = 'userName' | 'workTotalMinutes' | 'breakTotalMinutes'

export interface ISelectedEmployeeToCreateHours {
  employeeId: string
  employeeName: string
}

interface State {
  hours?: Array<{ userId: string; userName: string; hours: IEmployeeHour[] }>
  selectedEmployeeIds: string[]
  selectedEmployeeHour?: IEmployeeHour
  sort: { field: SortFields; direction: 'asc' | 'desc' }
  selectedEmployeeToCreateEmployeeHours: undefined | ISelectedEmployeeToCreateHours
}

const Fragment = (React as any).Fragment

@inject('currentCompanyStore', 'currentUserStore', 'reportFilterStore')
@observer
export class EmployeeHoursReport extends React.Component<Props, State> {
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.state = {
      selectedEmployeeIds: [],
      sort: {
        field: 'userName',
        direction: 'asc',
      },
      selectedEmployeeToCreateEmployeeHours: undefined,
    }
  }

  componentDidMount() {
    this.loadReport()
  }

  loadReport() {
    const { startDate, endDate, selectedLocationIds } = this.props.reportFilterStore!
    this.setState({ hours: undefined })
    const filter = {
      locationIds: selectedLocationIds,
      startDate: moment(startDate).startOf('day').toDate(),
      endDate: moment(endDate).endOf('day').toDate(),
    }
    const company = this.props.currentCompanyStore!.company!
    Track.startTimer('dashboard:reports_employeeHours')
    API.getAllEmployeeHours(company.id, filter).then((hours) => {
      Track.track('dashboard:reports_employeeHours', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ hours: hours })
    })
  }

  toggleUserId(userId: string) {
    const selectedEmployeeIds = this.state.selectedEmployeeIds
    if (selectedEmployeeIds.includes(userId)) {
      this.setState({ selectedEmployeeIds: selectedEmployeeIds.filter((e) => e !== userId) })
    } else {
      this.setState({ selectedEmployeeIds: [...selectedEmployeeIds, userId] })
    }
  }

  toggleSort(field: SortFields) {
    if (this.state.sort.field === field) {
      if (this.state.sort.direction === 'asc') {
        this.setState({
          sort: {
            field: field,
            direction: 'desc',
          },
        })
      } else {
        this.setState({
          sort: {
            field: field,
            direction: 'asc',
          },
        })
      }
    } else {
      this.setState({
        sort: {
          field: field,
          direction: 'desc',
        },
      })
    }
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const hours = this.state.hours

    if (hours) {
      const reportJSON: any[] = []

      hours.forEach((hour) => {
        const workTotalMinutes = hour.hours
          .filter((h) => h.type === 'work')
          .map((h) => h.totalMinutes)
          .reduce((a, b) => a + b, 0)
        const breakTotalMinutes = hour.hours
          .filter((h) => h.type === 'break')
          .map((h) => h.totalMinutes)
          .reduce((a, b) => a + b, 0)
        reportJSON.push({
          name: `${hour.userName}`,
          workTotalMinutes: `${parseFloat((workTotalMinutes / 60).toFixed(2))}`,
          breakTotalMinutes: `${parseFloat((breakTotalMinutes / 60).toFixed(2))}`,
        })
      })

      parseAndDownloadCSV(`employee-hours-report-${startDate}-to-${endDate}`, reportJSON)
    }
  }

  render() {
    const { hours, selectedEmployeeToCreateEmployeeHours } = this.state
    const canEditEmployeeHours = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_EDIT_EMPLOYEE_HOURS,
      this.company.id,
    )

    if (!hours) {
      return <LoadingAnimation />
    }

    const hoursWithTotalMinutes = hours.map((hour) => {
      const workTotalMinutes = hour.hours
        .filter((h) => h.type === 'work')
        .map((h) => h.totalMinutes)
        .reduce((a, b) => a + b, 0)
      const breakTotalMinutes = hour.hours
        .filter((h) => h.type === 'break')
        .map((h) => h.totalMinutes)
        .reduce((a, b) => a + b, 0)
      return Object.assign(hour, {
        workTotalMinutes: workTotalMinutes,
        breakTotalMinutes: breakTotalMinutes,
      })
    })

    const orderedHours = orderBy(
      hoursWithTotalMinutes,
      [this.state.sort.field],
      [this.state.sort.direction],
    )

    return (
      <>
        <Helmet>
          <title>Reports | Employee Hours</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <ReportFilter maxRangeMonths={12} />

        <div className='p-3 rounded background-white'>
          <b>Track your employee hours</b>
          <div>Hours are tracked via clocking in and out of the POS.</div>
          <div>
            Entries can be corrected by editing the start/end times, or by deleting incorrect
            entries.
          </div>
          <div>
            Breaks are tracked separately from work hours and are considered to be paid time.
          </div>
        </div>

        <br />

        <div className='rounded background-white p-3'>
          <button className='btn btn-small btn-light pull-right mb-3' onClick={() => this.export()}>
            Export as csv
          </button>
          <table className='table table-sm'>
            <tbody>
              <tr>
                <th>
                  <a onClick={() => this.toggleSort('userName')}>Employee</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleSort('workTotalMinutes')}>Work hours</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleSort('breakTotalMinutes')}>Break hours</a>
                </th>
                <th className='text-right'>Details</th>
              </tr>
              {orderedHours.map((hour) => {
                return (
                  <Fragment key={hour.userId}>
                    <tr>
                      <td>{hour.userName}</td>
                      <td className='text-right'>
                        {GrTime.convertMinsToHrsMins(hour.workTotalMinutes)}
                      </td>
                      <td className='text-right'>
                        {GrTime.convertMinsToHrsMins(hour.breakTotalMinutes)}
                      </td>
                      <td className='text-right'>
                        <a onClick={() => this.toggleUserId(hour.userId)}>Details</a>
                      </td>
                    </tr>
                    {this.state.selectedEmployeeIds.includes(hour.userId) && (
                      <>
                        <tr>
                          <td colSpan={4} className='text-right bg-light p-4'>
                            <table className='table table-sm mb-3'>
                              <thead className='thead thead-secondary'>
                                <tr className='table-light'>
                                  <th>POS</th>
                                  <th>Start</th>
                                  <th>End</th>
                                  <th>Hours</th>
                                  <th>Type</th>
                                  <th>Edit</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {hour.hours.map((h) => {
                                  return (
                                    <tr
                                      className={
                                        h.type === 'work' ? 'table-success' : 'table-warning'
                                      }
                                      key={h.id}
                                    >
                                      <td>{h.deviceName || '-'}</td>
                                      <td>{moment(h.startTime).format('LLLL')}</td>
                                      <td>
                                        {h.endTime
                                          ? moment(h.endTime).format('LLLL')
                                          : 'in progress'}
                                      </td>
                                      <td>{GrTime.convertMinsToHrsMins(h.totalMinutes)}</td>
                                      <td>{h.type}</td>
                                      <td>
                                        {canEditEmployeeHours ? (
                                          <a
                                            onClick={() =>
                                              this.setState({ selectedEmployeeHour: h })
                                            }
                                          >
                                            Edit
                                          </a>
                                        ) : (
                                          <span className='text-muted'>-</span>
                                        )}
                                      </td>
                                      <td>
                                        {h.totalMinutes > 720 && (
                                          <>
                                            <span
                                              className='fa fa-exclamation-circle text-danger'
                                              data-tip
                                              data-for={`warning-${h.id}`}
                                            />
                                            <Tooltip
                                              id={`warning-${h.id}`}
                                              place='right'
                                              effect='solid'
                                            >
                                              <div className='mb-1'>
                                                This value is very large, there may be an error with
                                                this entry.
                                              </div>
                                              <div>
                                                A common reason this happens is if an employee
                                                forgets to end a shift on a POS.
                                              </div>
                                              <div>
                                                If this entry is incorrect, it's recommended to edit
                                                or delete this entry.
                                              </div>
                                            </Tooltip>
                                          </>
                                        )}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>

                            <div className='text-right'>
                              <button
                                className='btn btn-light'
                                disabled={!canEditEmployeeHours}
                                onClick={() =>
                                  this.setState({
                                    selectedEmployeeToCreateEmployeeHours: {
                                      employeeId: hour.userId,
                                      employeeName: hour.userName,
                                    },
                                  })
                                }
                              >
                                Add hours
                              </button>
                            </div>
                          </td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>

          {this.state.selectedEmployeeHour !== undefined && (
            <EditEmployeeHourModal
              employeeHour={this.state.selectedEmployeeHour}
              onSubmit={() => {
                this.setState({ selectedEmployeeHour: undefined })
                this.loadReport()
              }}
              onClose={() => {
                this.setState({ selectedEmployeeHour: undefined })
              }}
            />
          )}
          {!!selectedEmployeeToCreateEmployeeHours && (
            <CreateEmployeeHourModal
              employee={selectedEmployeeToCreateEmployeeHours}
              onSubmit={() => {
                this.setState({ selectedEmployeeToCreateEmployeeHours: undefined })
                this.loadReport()
              }}
              onClose={() => this.setState({ selectedEmployeeToCreateEmployeeHours: undefined })}
            />
          )}
        </div>
      </>
    )
  }
}
